import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowUpRight, Check, Loader } from 'lucide-react'

import { cn } from '@/lib/utils'
import { guestOptions, roomTypeOptions } from '@/constants/contact'

type Status = 'idle' | 'sending' | 'sent' | 'error'

interface Values {
    firstName: string
    lastName: string
    email: string
    phone: string
    arrival: string
    departure: string
    guests: string
    roomType: string
    liftTickets: string
    roomPreferences: string
    message: string
    consent: boolean
    company: string
}

const EMPTY: Values = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    arrival: '',
    departure: '',
    guests: '',
    roomType: '',
    liftTickets: '',
    roomPreferences: '',
    message: '',
    consent: false,
    company: ''
}

/** Input chrome lifted from the original: cream-soft fill, 1px rule, 8px radius. */
const field = 'w-full rounded-[8px] border border-hairline bg-cream-soft px-5 py-4 text-[16px] font-medium leading-[1.2] text-ink outline-none transition-colors placeholder:text-ink-soft/60 focus:border-ink disabled:opacity-60'

const labelText = 'text-[14px] font-medium leading-none text-ink'

const helper = 'text-[14px] text-ink underline underline-offset-4 transition-colors hover:text-brand'

export const BookingForm: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>(EMPTY)
    const [status, setStatus] = useState<Status>('idle')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [notice, setNotice] = useState('')

    const update = (key: keyof Values) => (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ): void => {
        const target = event.target

        setValues((current) => ({
            ...current,
            [key]: target instanceof HTMLInputElement && target.type === 'checkbox'
                ? target.checked
                : target.value
        }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') return

        setStatus('sending')
        setErrors({})
        setNotice('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const payload = await response.json().catch(() => ({}))

            if (!response.ok) {
                setErrors(payload.fieldErrors ?? {})
                setNotice(payload.message ?? 'Something went wrong. Please try again.')
                setStatus('error')

                return
            }

            setValues(EMPTY)
            setStatus('sent')
        } catch {
            setNotice('We could not reach the front desk just now. Please try again in a moment.')
            setStatus('error')
        }
    }

    if (status === 'sent') {
        return (
            <div className='animate-in fade-in slide-in-from-bottom-4 flex flex-col items-center px-2 py-10 text-center duration-500 ease-out'>
                <span className='flex size-14 items-center justify-center rounded-full bg-brand text-cream'>
                    <Check className='size-6' />
                </span>

                <h3 className='mt-6 font-heading text-[34px] font-medium leading-[1.05] tracking-[-0.03em] text-brand'>
                    Inquiry received
                </h3>

                <p className='mt-3 max-w-[380px] text-[15px] leading-[1.6] text-ink-soft'>
                    Thank you. We have your request and will be in touch to confirm the details. For anything urgent, call the hotel at 802-775-1608.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-7 rounded-full border border-brand px-6 py-3 text-[15px] font-medium text-brand transition-colors hover:bg-brand hover:text-cream'
                >
                    Send another inquiry
                </button>
            </div>
        )
    }

    const sending = status === 'sending'

    const error = (key: string): ReactNode =>
        errors[key] ? (
            <span className='text-[13px] text-brand'>
                {errors[key]}
            </span>
        ) : null

    return (
        <form onSubmit={submit} noValidate className='grid grid-cols-1 gap-x-[10px] gap-y-5 md:grid-cols-2'>
            <label className='flex flex-col gap-3.5'>
                <span className={labelText}>
                    First Name*
                </span>

                <input
                    type='text'
                    value={values.firstName}
                    onChange={update('firstName')}
                    disabled={sending}
                    autoComplete='given-name'
                    placeholder='Enter your first name'
                    aria-invalid={Boolean(errors.firstName)}
                    className={cn(field, errors.firstName && 'border-brand')}
                />

                {error('firstName')}
            </label>

            <label className='flex flex-col gap-3.5'>
                <span className={labelText}>
                    Last Name
                </span>

                <input
                    type='text'
                    value={values.lastName}
                    onChange={update('lastName')}
                    disabled={sending}
                    autoComplete='family-name'
                    placeholder='Enter your last name'
                    className={field}
                />
            </label>

            <label className='flex flex-col gap-3.5 md:col-span-2'>
                <span className={labelText}>
                    Email*
                </span>

                <input
                    type='email'
                    value={values.email}
                    onChange={update('email')}
                    disabled={sending}
                    autoComplete='email'
                    placeholder='Enter your email address'
                    aria-invalid={Boolean(errors.email)}
                    className={cn(field, errors.email && 'border-brand')}
                />

                {error('email')}
            </label>

            <label className='flex flex-col gap-3.5 md:col-span-2'>
                <span className={labelText}>
                    Phone*
                </span>

                <input
                    type='tel'
                    value={values.phone}
                    onChange={update('phone')}
                    disabled={sending}
                    autoComplete='tel'
                    placeholder='Enter your phone number'
                    aria-invalid={Boolean(errors.phone)}
                    className={cn(field, errors.phone && 'border-brand')}
                />

                {error('phone')}
            </label>

            <label className='flex flex-col gap-3.5'>
                <span className={labelText}>
                    Arrival Date*
                </span>

                <input
                    type='date'
                    value={values.arrival}
                    onChange={update('arrival')}
                    disabled={sending}
                    aria-invalid={Boolean(errors.arrival)}
                    className={cn(field, errors.arrival && 'border-brand')}
                />

                {error('arrival')}
            </label>

            <label className='flex flex-col gap-3.5'>
                <span className={labelText}>
                    Departure Date*
                </span>

                <input
                    type='date'
                    value={values.departure}
                    onChange={update('departure')}
                    disabled={sending}
                    aria-invalid={Boolean(errors.departure)}
                    className={cn(field, errors.departure && 'border-brand')}
                />

                {error('departure')}
            </label>

            <label className='flex flex-col gap-3.5 md:col-span-2'>
                <span className={labelText}>
                    Guests*
                </span>

                <select
                    value={values.guests}
                    onChange={update('guests')}
                    disabled={sending}
                    aria-invalid={Boolean(errors.guests)}
                    className={cn(
                        field,
                        'appearance-none bg-[length:16px] bg-[right_20px_center] bg-no-repeat pr-12',
                        values.guests === '' && 'text-ink-soft/60',
                        errors.guests && 'border-brand'
                    )}
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2382625b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'m6 9 6 6 6-6\'/%3E%3C/svg%3E")' }}
                >
                    <option value='' disabled>
                        Select number of guests...
                    </option>

                    {guestOptions.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>

                {error('guests')}
            </label>

            <label className='flex flex-col gap-3.5 md:col-span-2'>
                <span className={labelText}>
                    Room Type*
                </span>

                <select
                    value={values.roomType}
                    onChange={update('roomType')}
                    disabled={sending}
                    aria-invalid={Boolean(errors.roomType)}
                    className={cn(
                        field,
                        'appearance-none bg-[length:16px] bg-[right_20px_center] bg-no-repeat pr-12',
                        values.roomType === '' && 'text-ink-soft/60',
                        errors.roomType && 'border-brand'
                    )}
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2382625b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'m6 9 6 6 6-6\'/%3E%3C/svg%3E")' }}
                >
                    <option value='' disabled>
                        Choose your preferred room...
                    </option>

                    {roomTypeOptions.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>

                {error('roomType')}
            </label>

            <div className='flex flex-col gap-3.5'>
                <label className='flex flex-col gap-3.5'>
                    <span className={labelText}>
                        Lift Tickets
                    </span>

                    <input
                        type='text'
                        value={values.liftTickets}
                        onChange={update('liftTickets')}
                        disabled={sending}
                        placeholder="How many, weekday or weekend..."
                        className={field}
                    />
                </label>

                <Link href='/packages' className={cn(helper, 'self-start')}>
                    Lift ticket prices
                </Link>
            </div>

            <div className='flex flex-col gap-3.5'>
                <label className='flex flex-col gap-3.5'>
                    <span className={labelText}>
                        Room Preferences
                    </span>

                    <input
                        type='text'
                        value={values.roomPreferences}
                        onChange={update('roomPreferences')}
                        disabled={sending}
                        placeholder="Balcony, patio, connecting..."
                        className={field}
                    />
                </label>

                <Link href='/#rooms' className={cn(helper, 'self-start')}>
                    View our rooms
                </Link>
            </div>

            <label className='flex flex-col gap-3.5 md:col-span-2'>
                <span className={labelText}>
                    Message*
                </span>

                <textarea
                    value={values.message}
                    onChange={update('message')}
                    disabled={sending}
                    rows={5}
                    placeholder='Write your message'
                    aria-invalid={Boolean(errors.message)}
                    className={cn(field, 'resize-y whitespace-normal', errors.message && 'border-brand')}
                />

                {error('message')}
            </label>

            <div className='flex items-start gap-2.5 md:col-span-2'>
                <span className='relative mt-0.5 flex size-4 shrink-0 items-center justify-center'>
                    <input
                        id='booking-consent'
                        type='checkbox'
                        checked={values.consent}
                        onChange={update('consent')}
                        disabled={sending}
                        className='peer size-4 appearance-none rounded-[4px] border border-hairline bg-cream-soft transition-colors checked:border-brand checked:bg-brand'
                    />

                    <Check
                        aria-hidden='true'
                        className='pointer-events-none absolute size-3 text-cream opacity-0 transition-opacity peer-checked:opacity-100'
                    />
                </span>

                <label htmlFor='booking-consent' className='text-[14px] leading-[1.45] text-ink'>
                    I agree that the Greenbrier Inn may use these details to reply to my inquiry.
                </label>
            </div>

            {errors.consent && (
                <span className='text-[13px] text-brand md:col-span-2'>
                    {errors.consent}
                </span>
            )}

            {/* Honeypot: never shown, so anything typed here came from a bot. */}
            <div aria-hidden='true' className='pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0'>
                <input
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.company}
                    onChange={update('company')}
                />
            </div>

            <div className='md:col-span-2'>
                <button
                    type='submit'
                    disabled={sending}
                    className='group relative flex h-[64px] w-full items-center justify-center rounded-full bg-brand text-[17px] font-medium text-cream transition-colors duration-300 hover:bg-ink disabled:opacity-60'
                >
                    <span className='inline-flex items-center gap-2'>
                        {sending && <Loader className='size-4 animate-spin' />}

                        {sending ? "Sending" : "Send your Inquiry"}
                    </span>

                    <span className='absolute right-[6px] top-1/2 flex size-[52px] -translate-y-1/2 items-center justify-center rounded-full bg-cream text-brand transition-transform duration-300 group-hover:rotate-45'>
                        <ArrowUpRight className='size-5' />
                    </span>
                </button>

                {notice && (
                    <p className='mt-4 rounded-[8px] border border-brand/30 bg-brand/5 px-4 py-3 text-[14px] text-brand'>
                        {notice}
                    </p>
                )}

                <p role='status' aria-live='polite' className='sr-only'>
                    {sending ? "Sending your inquiry" : ''}
                </p>
            </div>
        </form>
    )
}
